import React, { useEffect, useMemo, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Clock, Radio, Video, X } from "lucide-react";
import { formatDuration } from "@/lib/timing";

const MODE_LIVE = "live";
const MODE_RECORDED = "recorded";

const pad = (n) => String(n).padStart(2, "0");

// Returns { label, bgClass, textClass, blinking, onAir }
function stageFor(remainingSec) {
  if (remainingSec <= 0) {
    return { onAir: true, bgClass: "bg-red-600", textClass: "text-white", blinking: true };
  }
  if (remainingSec <= 60) {
    return { onAir: false, bgClass: "bg-red-600", textClass: "text-white", blinking: true };
  }
  if (remainingSec <= 300) {
    return { onAir: false, bgClass: "bg-pink-500", textClass: "text-white", blinking: false };
  }
  if (remainingSec <= 600) {
    return { onAir: false, bgClass: "bg-emerald-600", textClass: "text-white", blinking: false };
  }
  return { onAir: false, bgClass: "bg-[#152D4B]", textClass: "text-white", blinking: false };
}

function formatHMS(sec) {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h > 0) return `${h}:${pad(m)}:${pad(ss)}`;
  return `${pad(m)}:${pad(ss)}`;
}

// Convert ISO datetime <-> local datetime-local input value
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const tzOff = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOff).toISOString().slice(0, 16);
}
function localInputToIso(local) {
  if (!local) return "";
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

export default function EspelhoTimer({ episode, totalSec, approvedSec, onPatchEpisode }) {
  const startAt = episode.start_at || "";
  const mode = episode.broadcast_mode || MODE_LIVE;
  const artSec = Math.max(0, Number(episode.art_time_sec || 0));

  const [now, setNow] = useState(() => Date.now());
  const [dlgOpen, setDlgOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(() => isoToLocalInput(startAt));
  const [draftMode, setDraftMode] = useState(mode);

  // Tick every 500ms
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);

  const targetMs = startAt ? new Date(startAt).getTime() : null;
  const remaining = targetMs ? Math.round((targetMs - now) / 1000) : null;
  const stage = startAt ? stageFor(remaining) : null;

  // Difference between planned (art) and actual (totalSec from approved retrancas)
  // "tempo total" = soma de retrancas aprovadas (approvedSec)
  // "Buraco" if programa está mais curto que a arte (art > total → missing)
  // "Estouro" if programa está mais longo que a arte (total > art)
  const diff = approvedSec - artSec;
  const buraco = artSec > 0 && diff < 0 ? Math.abs(diff) : 0;
  const estouro = artSec > 0 && diff > 0 ? diff : 0;

  const saveStart = async () => {
    const iso = localInputToIso(draftStart);
    await onPatchEpisode({
      start_at: iso,
      broadcast_mode: draftMode,
    });
    setDlgOpen(false);
  };

  const clearStart = async () => {
    await onPatchEpisode({ start_at: "", broadcast_mode: "" });
    setDlgOpen(false);
  };

  const openDialog = () => {
    setDraftStart(isoToLocalInput(startAt));
    setDraftMode(mode || MODE_LIVE);
    setDlgOpen(true);
  };

  const setArtTime = async (newArt) => {
    await onPatchEpisode({ art_time_sec: Math.max(0, Math.floor(newArt)) });
  };

  // Arte em minutos (manual input)
  const [artMin, setArtMin] = useState(Math.floor(artSec / 60));
  const [artSec2, setArtSec2] = useState(artSec % 60);
  const artTimerRef = useRef(null);
  useEffect(() => {
    setArtMin(Math.floor(artSec / 60));
    setArtSec2(artSec % 60);
  }, [artSec]);
  const commitArt = (m, s) => {
    if (artTimerRef.current) clearTimeout(artTimerRef.current);
    artTimerRef.current = setTimeout(() => setArtTime(m * 60 + s), 400);
  };

  return (
    <>
      <div className="flex items-stretch gap-2" data-testid="espelho-timer">
        {/* Countdown / On-air pill */}
        {startAt ? (
          <button
            onClick={openDialog}
            data-testid="espelho-timer-pill"
            className={`${stage.bgClass} ${stage.textClass} ${stage.blinking ? "animate-pulse" : ""} rounded-sm px-3 py-1.5 flex items-center gap-2 min-w-[160px] text-left shadow-sm hover:brightness-110 transition`}
            title="Alterar horário / modo"
          >
            {stage.onAir ? (
              <Radio className="w-4 h-4" />
            ) : mode === MODE_RECORDED ? (
              <Video className="w-4 h-4" />
            ) : (
              <Clock className="w-4 h-4" />
            )}
            <div className="leading-tight flex-1">
              <div className="text-[9px] uppercase tracking-[0.25em] font-bold opacity-90">
                {stage.onAir
                  ? (mode === MODE_RECORDED ? "Gravando" : "No Ar")
                  : (mode === MODE_RECORDED ? "Gravação em" : "Entra em")}
              </div>
              <div className="font-mono text-base font-bold tracking-tight" data-testid="espelho-countdown">
                {stage.onAir ? "AGORA" : formatHMS(Math.max(0, remaining))}
              </div>
            </div>
          </button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            data-testid="espelho-set-start-btn"
            onClick={openDialog}
            className="rounded-sm h-auto py-1.5 px-3 border-[#152D4B] text-[#152D4B] hover:bg-[#152D4B] hover:text-white"
          >
            <Clock className="w-4 h-4 mr-2" />
            <div className="leading-tight text-left">
              <div className="text-[9px] uppercase tracking-[0.25em] font-bold">Horário</div>
              <div className="text-xs font-bold">Definir início</div>
            </div>
          </Button>
        )}

        {/* Tempo de arte + total + buraco/estouro */}
        <div className="hidden md:flex items-stretch gap-1 border border-zinc-200 rounded-sm px-2 py-1">
          <div className="pr-2 border-r border-zinc-100">
            <div className="text-[9px] uppercase tracking-[0.25em] font-bold text-zinc-500">Arte</div>
            <div className="flex items-center gap-0.5 font-mono">
              <Input
                data-testid="espelho-art-min"
                type="number" min={0}
                value={artMin}
                onChange={(e) => {
                  const m = Math.max(0, parseInt(e.target.value || "0", 10));
                  setArtMin(m);
                  commitArt(m, artSec2);
                }}
                className="w-10 h-5 border-0 focus-visible:ring-0 p-0 text-xs text-right"
              />
              <span className="text-zinc-400 text-xs">:</span>
              <Input
                data-testid="espelho-art-sec"
                type="number" min={0} max={59}
                value={artSec2}
                onChange={(e) => {
                  const s = Math.min(59, Math.max(0, parseInt(e.target.value || "0", 10)));
                  setArtSec2(s);
                  commitArt(artMin, s);
                }}
                className="w-10 h-5 border-0 focus-visible:ring-0 p-0 text-xs text-right"
              />
            </div>
          </div>
          <div className="px-2 border-r border-zinc-100">
            <div className="text-[9px] uppercase tracking-[0.25em] font-bold text-zinc-500">Total</div>
            <div className="font-mono text-xs font-bold text-[#152D4B]" data-testid="espelho-total-time">
              {formatDuration(approvedSec)}
            </div>
          </div>
          {artSec > 0 && buraco > 0 && (
            <div className="px-2" data-testid="espelho-buraco">
              <div className="text-[9px] uppercase tracking-[0.25em] font-bold text-amber-600">Buraco</div>
              <div className="font-mono text-xs font-bold text-amber-700">
                -{formatDuration(buraco)}
              </div>
            </div>
          )}
          {artSec > 0 && estouro > 0 && (
            <div className="px-2" data-testid="espelho-estouro">
              <div className="text-[9px] uppercase tracking-[0.25em] font-bold text-red-600">Estouro</div>
              <div className="font-mono text-xs font-bold text-red-700">
                +{formatDuration(estouro)}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="rounded-sm" data-testid="espelho-start-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Horário de início</DialogTitle>
            <DialogDescription>
              Configure o horário em que o programa entrará no ar ou começará a gravação.
              A contagem regressiva muda de cor conforme se aproxima do início.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                Modo
              </Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <button
                  type="button"
                  data-testid="mode-live"
                  onClick={() => setDraftMode(MODE_LIVE)}
                  className={`rounded-sm border p-3 text-left transition ${draftMode === MODE_LIVE ? "border-[#152D4B] bg-[#152D4B]/5" : "border-zinc-200 hover:border-zinc-400"}`}
                >
                  <div className="flex items-center gap-2 font-bold text-sm">
                    <Radio className="w-4 h-4 text-[#152D4B]" /> Ao vivo
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">Ao zerar aparece "NO AR"</div>
                </button>
                <button
                  type="button"
                  data-testid="mode-recorded"
                  onClick={() => setDraftMode(MODE_RECORDED)}
                  className={`rounded-sm border p-3 text-left transition ${draftMode === MODE_RECORDED ? "border-[#152D4B] bg-[#152D4B]/5" : "border-zinc-200 hover:border-zinc-400"}`}
                >
                  <div className="flex items-center gap-2 font-bold text-sm">
                    <Video className="w-4 h-4 text-[#152D4B]" /> Gravado
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">Ao zerar aparece "GRAVANDO"</div>
                </button>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                Data e horário de início
              </Label>
              <Input
                data-testid="start-at-input"
                type="datetime-local"
                value={draftStart}
                onChange={(e) => setDraftStart(e.target.value)}
                className="rounded-sm border-zinc-300 mt-1.5"
              />
              <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
                Legenda da regressiva:
                <span className="inline-block ml-1 w-2 h-2 bg-[#152D4B]" /> &gt;10m
                <span className="inline-block ml-2 w-2 h-2 bg-emerald-600" /> ≤10m
                <span className="inline-block ml-2 w-2 h-2 bg-pink-500" /> ≤5m
                <span className="inline-block ml-2 w-2 h-2 bg-red-600 animate-pulse" /> ≤1m
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            {startAt && (
              <Button
                variant="outline"
                data-testid="clear-start-btn"
                onClick={clearStart}
                className="rounded-sm text-zinc-500 border-zinc-300"
              >
                <X className="w-4 h-4 mr-1" /> Limpar
              </Button>
            )}
            <Button
              data-testid="save-start-btn"
              onClick={saveStart}
              disabled={!draftStart}
              className="rounded-sm bg-[#152D4B] hover:bg-[#0E1F36] text-white"
            >
              Salvar horário
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
